import { getSharedAudioContext } from './callAudioGraph';

/**
 * Web Audio API 및 Vibration API 기반 통화음 유틸리티
 * 외부 오디오 파일 및 라이브러리 없이 브라우저 내장 API 만을 사용합니다.
 */

let ringInterval = null;
let ringbackInterval = null;
let vibeInterval = null;

// 현재 재생 중인 오디오 노드 추적 (클릭 노이즈 방지용 부드러운 감쇠 정지)
let activeSoundNodes = [];

/**
 * 전역 공유 싱글톤 AudioContext 인스턴스 획득 (자동재생 정책 대응)
 * [사유] iOS 에서 사용자 제스처로 확보한 오디오 권한을 두 모듈이 중복 획득하지 않도록 하기 위함.
 */
function getAudioContext() {
  return getSharedAudioContext();
}

/**
 * 듀얼 주파수 톤 생성 및 재생 (클릭 방지용 페이드인/페이드아웃 포함)
 */
function playDualTone(freq1, freq2, durationSec = 1.0, gainLevel = 0.2) {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gainNode = ctx.createGain();

    osc1.type = 'sine';
    osc2.type = 'sine';
    osc1.frequency.setValueAtTime(freq1, ctx.currentTime);
    osc2.frequency.setValueAtTime(freq2, ctx.currentTime);

    // 부드러운 어택/디케이 (클릭 잡음 방지)
    const attack = 0.05;
    const release = 0.05;
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(gainLevel, ctx.currentTime + attack);
    gainNode.gain.setValueAtTime(gainLevel, ctx.currentTime + durationSec - release);
    gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + durationSec);

    osc1.connect(gainNode);
    osc2.connect(gainNode);
    gainNode.connect(ctx.destination);

    const nodeEntry = { osc1, osc2, gainNode, ctx };
    activeSoundNodes.push(nodeEntry);

    osc1.start(ctx.currentTime);
    osc2.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + durationSec);
    osc2.stop(ctx.currentTime + durationSec);

    // 재생 완료 시 활성 노드 목록에서 제거
    setTimeout(() => {
      activeSoundNodes = activeSoundNodes.filter((item) => item !== nodeEntry);
      try { osc1.disconnect(); } catch (e) {}
      try { osc2.disconnect(); } catch (e) {}
      try { gainNode.disconnect(); } catch (e) {}
    }, (durationSec + 0.1) * 1000);
  } catch (err) {
    console.warn('[callSounds] 톤 재생 예외 (통화 지속):', err);
  }
}

/**
 * 수신 벨소리 시작 (울림 1초 -> 쉼 2초 반복, 3초 주기)
 * 일반 전화 수신 벨 느낌 (440Hz + 480Hz) + 진동 (1000ms 진동, 2000ms 쉼)
 */
export function startRingtone() {
  stopAllCallSounds();

  // 1) 즉시 1회 재생
  playDualTone(440, 480, 1.0, 0.25);

  // 2) 진동 즉시 1회 (지원 기기만)
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try {
      navigator.vibrate([1000, 2000]);
    } catch (e) {}
  }

  // 3) 3초 주기 반복
  ringInterval = setInterval(() => {
    playDualTone(440, 480, 1.0, 0.25);
  }, 3000);

  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    vibeInterval = setInterval(() => {
      try {
        navigator.vibrate([1000, 2000]);
      } catch (e) {}
    }, 3000);
  }
}

/**
 * 발신자 대기음 시작 (통화 연결 대기음 "뚜르르" 1초 -> 쉼 2초 반복)
 * 수신음과 구분되는 부드러운 톤 (400Hz + 450Hz, 볼륨 0.12)
 */
export function startRingbackTone() {
  stopAllCallSounds();

  // 1) 즉시 1회 재생
  playDualTone(400, 450, 1.0, 0.12);

  // 2) 3초 주기 반복
  ringbackInterval = setInterval(() => {
    playDualTone(400, 450, 1.0, 0.12);
  }, 3000);
}

/**
 * 모든 통화 사운드 및 진동 즉시 정지
 * 
 * [클릭 노이즈 제거 원칙]
 * 오실레이터를 파형이 0이 아닌 지점에서 즉시 stop()하면 "삐/뚝" 클릭 노이즈가 발생하므로,
 * GainNode의 gain을 약 40ms(0.04초)에 걸쳐 0으로 부드럽게 감쇠시킨 후 stop() 및 disconnect()를 수행합니다.
 */
export function stopAllCallSounds() {
  // 1. 반복 재생 타이머 즉시 해제
  if (ringInterval) {
    clearInterval(ringInterval);
    ringInterval = null;
  }
  if (ringbackInterval) {
    clearInterval(ringbackInterval);
    ringbackInterval = null;
  }
  if (vibeInterval) {
    clearInterval(vibeInterval);
    vibeInterval = null;
  }

  // 2. 진동 즉시 정지
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try {
      navigator.vibrate(0);
    } catch (e) {}
  }

  // 3. 현재 울리고 있는 모든 톤 노드를 40ms 페이드아웃 감쇠 후 안전하게 정지
  if (activeSoundNodes.length > 0) {
    const nodesToStop = [...activeSoundNodes];
    activeSoundNodes = [];

    nodesToStop.forEach(({ osc1, osc2, gainNode, ctx }) => {
      try {
        if (ctx && ctx.state === 'running') {
          const now = ctx.currentTime;
          const fadeDuration = 0.04; // 40ms 감쇠
          gainNode.gain.cancelScheduledValues(now);
          gainNode.gain.setValueAtTime(gainNode.gain.value, now);
          gainNode.gain.linearRampToValueAtTime(0, now + fadeDuration);

          setTimeout(() => {
            try { osc1.stop(); } catch (e) {}
            try { osc2.stop(); } catch (e) {}
            try { osc1.disconnect(); } catch (e) {}
            try { osc2.disconnect(); } catch (e) {}
            try { gainNode.disconnect(); } catch (e) {}
          }, 50);
        } else {
          try { osc1.stop(); } catch (e) {}
          try { osc2.stop(); } catch (e) {}
          try { osc1.disconnect(); } catch (e) {}
          try { osc2.disconnect(); } catch (e) {}
          try { gainNode.disconnect(); } catch (e) {}
        }
      } catch (err) {
        console.warn('[callSounds] 사운드 감쇠 정지 예외:', err);
      }
    });
  }
}
